// The engine's health at the foot of the spine (UX review, 30 Oct 2026): silent while everything works, one small line
// when a part of Prospects' or Conversations' engine needs the owner, linking to Settings › Automations.
// It replaces the agents indicator that sat at the top of every page: the engine's state is for when something is wrong.
import { Link } from "react-router-dom";
import type { Crm } from "../lib/crm";
import { loadModuleAgents } from "../lib/moduleAgents";
import { plural } from "../lib/format";
import { useAsync } from "../lib/useAsync";

export const SYSTEM_PATH = "/settings/automations";

/** Failing parts and blockers across the modules the owner works in, or null when they could not be read. */
export async function engineNeeds(crm: Crm): Promise<number | null> {
  const all = await Promise.all((["find", "connect"] as const).map((m) => loadModuleAgents(crm, m).catch(() => null)));
  const read = all.filter((a): a is NonNullable<typeof a> => a !== null && a.readable);
  return read.length ? read.reduce((n, a) => n + a.needs, 0) : null;
}

export function SpineHealth({ crm }: { crm: Crm }) {
  const needs = useAsync(() => engineNeeds(crm), [crm]);
  const n = needs.data ?? 0;
  if (n === 0) return null;
  return (
    <Link to={SYSTEM_PATH} className="spine-health" aria-label={`Engine: ${n} ${plural(n, "part")} need${n === 1 ? "s" : ""} you. Open Automations`}>
      <span className="spine-health-dot" aria-hidden="true" />
      {n} {plural(n, "part")} need{n === 1 ? "s" : ""} you
    </Link>
  );
}
